import { useAtom, useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  appendFiveSegDriverTop5Block,
  appendFiveSegSegmentTop5Block,
  appendSampleThresholdBlock,
  createFiveSegmentIndicatorLabelMap,
  filterVisibleAnswersArrayData,
  filterVisibleCustomerAttributesArrayData,
  type FiveSegmentDisplayColumnKey,
  fiveSegmentIndicators,
  type FiveSegmentSortColumnKey,
  fiveSegmentThresholdInputs,
  isImageQuestionText,
} from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import {
  SortButton,
  type SortButtonProps,
} from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { useKeyedAtomEntry } from '~/hooks/jotai'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  type Brand,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  type DisplayColumnItem,
  exists,
  fiveSegmentLabelI18nKeyMap,
  fiveSegments,
  formatFilterTextGroups,
  serializeTableToText,
} from '~/utils'

import {
  dataDisplayTypeAtom,
  fiveSegmentCommentAtom,
  fiveSegmentHiddenColumnKeysAtom,
  fiveSegmentSortValueAtom,
  hiddenRowIdsAtom,
  savedFiveSegmentCommentAtom,
} from '../atoms'
import { useRestoringParams } from '../hooks/restoringParams/useRestoringParams'
import { useDisplayRowItem } from '../hooks/useDisplayRowItem'
import { useExpandText } from '../hooks/useExpandText'
import { useFigureInfo } from '../hooks/useFigureInfo'
import { useFiveSegmentsAnswersArrayData } from '../hooks/useFiveSegmentsAnswersArrayData'
import { useFiveSegmentsCustomerAttributesArrayData } from '../hooks/useFiveSegmentsCustomerAttributesArrayData'
import { useFiveSegmentsTableColumns } from '../hooks/useFiveSegmentsTableColumns'
import { useFiveSegmentsTableInfo } from '../hooks/useFiveSegmentsTableInfo'
import { useFiveSegmentsTotalData } from '../hooks/useFiveSegmentsTotalData'
import { useSurveyId } from '../hooks/useSurveyId'

export type FiveSegmentsTableProps = {
  brand: Brand
  className?: string
}

// useLogEvent()はAmplitudeProviderの子孫でないと withSuffix が効かないため、
// Providerをレンダーする側とuseLogEvent()を呼ぶ側を分離する
export const FiveSegmentsTable = (props: FiveSegmentsTableProps) => (
  <AmplitudeProvider analysisName="customer data" eventNameSuffix="_5segs">
    <FiveSegmentsTableInner {...props} />
  </AmplitudeProvider>
)

const FiveSegmentsTableInner = ({
  brand,
  className,
}: FiveSegmentsTableProps) => {
  const [comment, setComment] = useKeyedAtomEntry(
    fiveSegmentCommentAtom,
    `${brand.id}`,
  )
  const [savedComment, setSavedComment] = useKeyedAtomEntry(
    savedFiveSegmentCommentAtom,
    `${brand.id}`,
  )
  const { generate, isGenerating } = useGenerateSingleComment()
  const surveyId = useSurveyId()
  const { t } = useTranslation()
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)

  const fiveSegmentIndicatorLabelMap = React.useMemo(
    () => createFiveSegmentIndicatorLabelMap(t),
    [t],
  )

  const sortButtonItems: SortButtonProps<FiveSegmentSortColumnKey>['items'] =
    React.useMemo(
      () => [
        { columnKey: 'total', label: t('overall') },
        ...R.reverse(fiveSegments).map((fiveSegment) => ({
          columnKey: fiveSegment,
          label: t(fiveSegmentLabelI18nKeyMap[fiveSegment]),
        })),
        ...fiveSegmentIndicators.map((indicator) => ({
          columnKey: indicator,
          label: fiveSegmentIndicatorLabelMap[indicator],
        })),
      ],
      [fiveSegmentIndicatorLabelMap, t],
    )

  const columns = useFiveSegmentsTableColumns()
  const tableInfo = useFiveSegmentsTableInfo(brand)
  const totalData = useFiveSegmentsTotalData(brand)
  const answersArrayData = useFiveSegmentsAnswersArrayData(brand)
  const customerAttributesArrayData =
    useFiveSegmentsCustomerAttributesArrayData(brand)

  const { handleTextExpandedChange, isTextExpanded, textLineCount } =
    useExpandText(tableInfo)

  const figureInfo = useFigureInfo(brand)
  const filterTextGroups = useSurveyQuerySurveyFilterTextGroups()
  const restoringParams = useRestoringParams(`${brand.id}`)
  const image = (
    <Table
      columns={columns}
      tableInfo={tableInfo}
      textLineCount={textLineCount}
    />
  )

  const [sortValue, setSortValue] = useAtom(fiveSegmentSortValueAtom)

  const displayRowItems = useDisplayRowItem(brand, '5segs')
  const [hiddenRowIds, setHiddenRowIds] = useAtom(hiddenRowIdsAtom)
  // 表テキストは useFiveSegmentsTableInfo が非表示行を除いた結果をシリアライズする。
  // Top5 ブロックも同じ可視行から作らないと、非表示にした項目がブロックにだけ残り、
  // 「表から選び直さない」契約の LLM がそれを書き写してしまう。
  const visibleAnswersArrayData = React.useMemo(
    () => filterVisibleAnswersArrayData(answersArrayData, hiddenRowIds),
    [answersArrayData, hiddenRowIds],
  )
  const visibleCustomerAttributesArrayData = React.useMemo(
    () =>
      filterVisibleCustomerAttributesArrayData(
        customerAttributesArrayData,
        hiddenRowIds,
      ),
    [customerAttributesArrayData, hiddenRowIds],
  )

  const [hiddenColumnKeys, setHiddenColumnKeys] = useAtom(
    fiveSegmentHiddenColumnKeysAtom,
  )
  const displayColumnItems = React.useMemo<
    DisplayColumnItem<FiveSegmentDisplayColumnKey>[]
  >(
    () => [
      {
        columnKey: 'total',
        isVisible: !hiddenColumnKeys.includes('total'),
        label: t('overall'),
      },
      ...R.reverse(fiveSegments).map((fiveSegment) => ({
        columnKey: fiveSegment,
        isVisible: !hiddenColumnKeys.includes(fiveSegment),
        label: t(fiveSegmentLabelI18nKeyMap[fiveSegment]),
      })),
      ...fiveSegmentIndicators.map((indicator) => ({
        columnKey: indicator,
        isVisible: !hiddenColumnKeys.includes(indicator),
        label: fiveSegmentIndicatorLabelMap[indicator],
      })),
    ],
    [hiddenColumnKeys, fiveSegmentIndicatorLabelMap, t],
  )

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: brand.name,
      filterDescription:
        filterTextGroups.length === 0
          ? ''
          : formatFilterTextGroups(filterTextGroups),
      filterPatternIndex: 0,
      outputType: 'standardAnalysis',
      segmentMode: 'fiveSegs',
      surveyId,
      // Only emit the threshold block when cells are percentages; the analyzer's sample-size guard compares item %, so non-% display modes would mismatch. Absent block ⇒ analyzer says nothing about sample size (by contract).
      // The driver-Top5 block is appended in EVERY display mode: it is computed from
      // ratios (not from the displayed cells), and the prompt's pts wording matches it.
      // セグメント別%Top5ブロックは全設問に付ける (場合分けAの選択・読み飛ばし対策)。
      tabulationData: appendFiveSegSegmentTop5Block(
        appendFiveSegDriverTop5Block(
          dataDisplayType === 'percentage'
            ? appendSampleThresholdBlock(
                serializeTableToText(columns, tableInfo),
                fiveSegmentThresholdInputs(totalData.counts, t),
              )
            : serializeTableToText(columns, tableInfo),
          // この画面には設問種別のメタデータが無いので、設問文でゲートする
          // (通常設問にブロックを付けると場合分けBへ誤誘導される — appendDriverTop5Block.ts 参照)。
          visibleAnswersArrayData.filter(
            (rows) =>
              0 < rows.length &&
              isImageQuestionText(rows[0].question.question_text),
          ),
          t,
          hiddenColumnKeys,
        ),
        visibleAnswersArrayData,
        t,
        hiddenColumnKeys,
        // 顧客属性の行 (ライフステージ等) も属性グループごとの節として載せる。
        visibleCustomerAttributesArrayData,
      ),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    generate,
    logEvent,
    brand.name,
    filterTextGroups,
    surveyId,
    dataDisplayType,
    columns,
    tableInfo,
    totalData,
    visibleAnswersArrayData,
    visibleCustomerAttributesArrayData,
    hiddenColumnKeys,
    t,
    setComment,
  ])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        className={className}
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              columnItems={displayColumnItems}
              isTextExpanded={isTextExpanded}
              isVisibleExpandTextCheckbox={true}
              rowItems={displayRowItems}
              onHiddenColumnChange={setHiddenColumnKeys}
              onHiddenRowChange={setHiddenRowIds}
              onTextExpandedChange={handleTextExpandedChange}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />
          </>
        }
      >
        <Table
          columns={columns}
          stickyColumns={3}
          tableInfo={tableInfo}
          textLineCount={textLineCount}
        />
      </TableContainer>
    </>
  )
}
